import { Product, ProductVariant, ProductVariantModel } from '../types';

/**
 * Maps a database product record (from Supabase) to the frontend Product interface.
 * Consolidates mapping logic in one place for API calls and Realtime events.
 */
export const mapDbProductToFrontend = (dbProduct: any): Product => {
    // Determine overall in_stock status based on variants
    // A product is in stock if AT LEAST ONE of its variants has AT LEAST ONE model in stock
    let inStock = false;
    if (dbProduct.variants && Array.isArray(dbProduct.variants)) {
        inStock = dbProduct.variants.some((v: any) =>
            v.models && Array.isArray(v.models) && v.models.some((m: any) => m.in_stock)
        );
    }

    // Default fallback if no images exist anywhere
    let defaultImage = dbProduct.image || 'https://via.placeholder.com/400x300?text=Sin+Imagen';
    let allImages: string[] = dbProduct.images || [];

    // Fallback to variant images ONLY if product has no main images
    if (!dbProduct.image && dbProduct.variants && Array.isArray(dbProduct.variants)) {
        for (const variant of dbProduct.variants) {
            if (variant.image_url && defaultImage.includes('placeholder')) {
                defaultImage = variant.image_url;
            }
        }
    }

    if ((!dbProduct.images || dbProduct.images.length === 0) && dbProduct.variants && Array.isArray(dbProduct.variants)) {
        for (const variant of dbProduct.variants) {
            if (variant.images && Array.isArray(variant.images)) {
                allImages = [...allImages, ...variant.images];
            }
        }
    }

    return {
        id: dbProduct.id,
        name: dbProduct.name,
        slug: dbProduct.slug,
        description: dbProduct.description || '',
        brand_id: dbProduct.brand_id,
        category_id: dbProduct.category_id,
        is_magsafe: dbProduct.is_magsafe || false,
        is_premium: dbProduct.is_premium || false,
        base_sku: dbProduct.base_sku || '',
        is_active: dbProduct.is_active,
        is_last_units: dbProduct.is_last_units || false,

        // Frontend virtual properties mapped from relations
        category: dbProduct.category?.name || '',
        make: dbProduct.brand?.name || '',

        image: defaultImage,
        images: Array.from(new Set(allImages)), // Unique images
        price: dbProduct.price || 0,

        stock: {
            'online': inStock
        },

        variants: (dbProduct.variants || [])
            .sort((a: any, b: any) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime())
            .map((v: any) => ({
                id: v.id,
                product_id: v.product_id,
                color_name: v.color_name,
                color_hex: v.color_hex,
                color_code: v.color_code,
                variant_sku: v.variant_sku,
                image_url: v.image_url,
                texture_url: v.texture_url,
                images: v.images || [],
                is_active: v.is_active,
                models: (v.models || []).map((m: any) => ({
                    id: m.id,
                    variant_id: m.variant_id,
                    model_id: m.model_id,
                    in_stock: m.in_stock,
                    is_active: m.is_active,
                    image_url: m.image_url,
                    model_name: m.model?.name || ''
                }))
            }))
    };
};

export const mapFrontendToDbProduct = (product: Partial<Product>): any => {
    return {
        ...(product.id ? { id: product.id } : {}),
        name: product.name,
        slug: product.slug,
        description: product.description,
        brand_id: product.brand_id,
        category_id: product.category_id,
        is_magsafe: product.is_magsafe ?? false,
        is_premium: product.is_premium ?? false,
        base_sku: product.base_sku,
        is_active: product.is_active ?? true,
        price: product.price ?? 0,
        is_last_units: product.is_last_units ?? false,
        image: product.image,
        images: product.images
    };
};
